import * as Sentry from '@sentry/react';
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  ArrowRight,
  Building,
  Loader
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import authService from "../../api/authService";
import jobSearchApi from "../../api/jobSearch";
import subscriptionApi from "../../api/subscription";
import Button from "../../components/common/Button";
import JobSearchCard from "../../components/professional/JobSearchCard";
import SearchForm from "../../components/professional/SearchForm";
import SideFilters from "../../components/professional/SideFilters";
import useFieldChoices from "../../hooks/useFieldChoices";
import { useAuthStore } from "../../store/authStore.js";

const JobSearchPage = ({ onNavigate }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAuthStore(s => s.user);
  const [isLoadingCategory, setIsLoadingCategory] = useState(user?.role === "professional");
  // const [jobs, setJobs] = useState([]);
  const [savedJobsMap, setSavedJobsMap] = useState(new Map());
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    q: "",
    location: "",
    department: "",
    job_type: "",
    experience_level: "",
    // is_remote: false,
    aircraft_type: "",
    min_salary: "",
    max_salary: "",
    page: page,
    pilot_type: "",
    total_flight_hours: "",
    aviation_category: ""
  });

  const { fieldChoicesError, fieldChoices } = useFieldChoices()

  if (fieldChoicesError) {
    Sentry.captureException(fieldChoicesError, "Field choices fetch error")
  }

  const { data: mySubscription, error: mySubscriptionError, isLoading: mySubscriptionLoading } = useQuery({
    queryKey: ["mySubscription"],
    queryFn: async () => {
      const res = await subscriptionApi.getMySubscription();
      return res.data;
    },
    enabled: !!user,
  });

  if (mySubscriptionError) {
    Sentry.captureException(mySubscription, "Error while fetching my subscriptions")
  }
  const { has_subscription = false } = mySubscription || {}

  const fetchSavedJobs = useCallback(async () => {
    if (!user) return;
    try {
      const response = await authService.getSavedSearches();
      const newMap = new Map();
      response.data.forEach((savedSearch) => {
        const jobIdMatch = savedSearch.query.match(/job_id:(\d+)/);
        if (jobIdMatch) {
          newMap.set(parseInt(jobIdMatch[1], 10), savedSearch.id);
        }
      });
      setSavedJobsMap(newMap);
    } catch (err) {
      console.error("Could not fetch saved jobs list.");
    }
  }, [user]);

  // const fetchJobs = useCallback(async (searchParams) => {
  //   setError(null);
  //   try {
  //     setLoading(true);
  //     const response = await jobSearchApi.searchJobs(searchParams);
  //     // const nonExpiredJobs = response.data.results.filter((job) => new Date(job.expiry_date) > new Date())
  //     setJobs(response.data.results || []);
  //     setPagination((prev) => ({
  //       ...prev,
  //       count: response.data.count,
  //       next: response.data.next,
  //       previous: response.data.previous,
  //     }));
  //   } catch (err) {
  //     console.error(err);
  //     setError("Failed to load job listings. Please try again later.");
  //   } finally {
  //     setLoading(false);
  //   }
  // }, []);

  useEffect(() => {
    fetchSavedJobs();
  }, [fetchSavedJobs]);

  const getProfessionalCategory = (data) => {
    if (!data) return "";
    const spec = data.specialization?.toLowerCase() || "";
    if (spec.includes("pilot")) return "pilot";
    if (spec.includes("engineer") || spec.includes("maintenance")) return "engineer";
    if (spec.includes("cabin") || spec.includes("attendant")) return "cabin_crew";
    if (spec.includes("atc") || spec.includes("traffic")) return "atc";
    if (spec.includes("dispatch") || spec.includes("ops")) return "flight_ops";
    if (spec.includes("medical") || spec.includes("nurse") || spec.includes("paramedic") || spec.includes("ame")) return "medical";

    for (const r of data.professional_roles || []) {
      const cat = r.aviation_category?.toLowerCase() || "";
      if (cat === "pilot") return "pilot";
      if (cat === "engineer") return "engineer";
      if (cat === "cabin_crew") return "cabin_crew";
    }

    for (const cat of data.aviation_categories || []) {
      if (cat.flight_crew || cat.pilot_type) return "pilot";
      if (cat.cabin_inflight) return "cabin_crew";
    }

    for (const lic of data.licenses || []) {
      const type = lic.license_type?.toLowerCase() || "";
      if (type.includes("pilot") || type.includes("atpl") || type.includes("cpl") || type.includes("ppl")) return "pilot";
      if (type.includes("engineer") || type.includes("ame") || type.includes("ap")) return "engineer";
      if (type.includes("cabin") || type.includes("attendant")) return "cabin_crew";
    }

    return "";
  };

  useEffect(() => {
    const loadProfileAndSetCategory = async () => {
      if (user?.role === "professional") {
        try {
          const res = await authService.getProfessionalProfile();
          const category = getProfessionalCategory(res.data);
          if (category) {
            setFilters((prev) => ({ ...prev, aviation_category: category }));
          }
        } catch (err) {
          console.error("Failed to load profile for category matching:", err);
        } finally {
          setIsLoadingCategory(false);
        }
      } else {
        setIsLoadingCategory(false);
      }
    };
    loadProfileAndSetCategory();
  }, [user]);

  const {
    data: jobsResponse,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["job-search", filters],
    queryFn: async () => {
      const res = await jobSearchApi.searchJobs(filters);
      return res.data;
    },
    enabled: !isLoadingCategory,
    keepPreviousData: true,
  });

  const jobs = jobsResponse ? jobsResponse.results : [];
  const pagination = {
    count: jobsResponse?.count || 0,
    next: jobsResponse?.next || null,
    previous: jobsResponse?.previous || null,
    currentPage: page,
  };

  // useEffect(() => {
  //   setFilters(searchParams.filtersFromUrl);
  // }, [searchParams.filtersFromUrl]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  // const applyFilters = () => {
  //   const params = new URLSearchParams();
  //   Object.entries(filters).forEach(([key, value]) => {
  //     if (value !== "" && value !== null && value !== undefined) {
  //       if (typeof value === "boolean") {
  //         params.set(key, value.toString());
  //       } else if (typeof value === "string" && value.length > 0) {
  //         params.set(key, value);
  //       } else if (typeof value === "number") {
  //         params.set(key, value.toString());
  //       }
  //     }
  //   });
  //   params.set("page", "1");
  //   navigate(`?${params.toString()}`);
  // };

  const resetFilters = () => {
    setFilters({
      q: "",
      location: "",
      department: "",
      job_type: "",
      experience_level: "",
      //   is_remote: false,
      aircraft_type: "",
      page: 1,
      pilot_type: "",
      total_flight_hours: "",
      aviation_category: filters.aviation_category // preserve auto-detected category on reset
    });
  };

  const handlePageChange = (newPage) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
    setPage(newPage);
  };

  const handleSaveToggle = async (job) => {
    if (!user) {
      navigate("/signin");
      return;
    }
    const isSaved = savedJobsMap.has(job.id);
    const newMap = new Map(savedJobsMap);

    if (isSaved) {
      const savedSearchId = savedJobsMap.get(job.id);
      newMap.delete(job.id);
      setSavedJobsMap(newMap);
      try {
        await authService.unsaveJob(savedSearchId);
      } catch (err) {
        newMap.set(job.id, savedSearchId);
        setSavedJobsMap(newMap); // Revert on failure
      }
    } else {
      // Optimistically set a temporary value
      newMap.set(job.id, "saving...");
      setSavedJobsMap(newMap);
      try {
        const response = await authService.saveJob(job);
        newMap.set(job.id, response.data.id);
        setSavedJobsMap(newMap);
      } catch (err) {
        newMap.delete(job.id);
        setSavedJobsMap(newMap); // Revert on failure
      }
    }
  };

  return (
    <div className="bg-secondary-50 min-h-screen">
      <div className="container mx-auto px-4 sm:px-6 py-8">
        <SearchForm
          // applyFilters={applyFilters}
          filters={filters}
          handleFilterChange={handleFilterChange}
        />

        <div className="grid lg:grid-cols-12 gap-8">
          <SideFilters
            filters={filters}
            handleFilterChange={handleFilterChange}
            resetFilters={resetFilters}
            pilotTypes={fieldChoices?.pilot_type_choices}
            aviationCategoryChoices={fieldChoices?.aviation_category_choices}
          // applyFilters={applyFilters}
          />
          <main className="lg:col-span-8 xl:col-span-9">
            <div className="mb-6">
              <h2 className="text-xl font-semibold">
                Showing {pagination.count} Job Openings
              </h2>
            </div>
            {isLoading || mySubscriptionLoading ? (
              <div className="text-center py-20 flex flex-col items-center">
                <Loader className="w-12 h-12 text-primary-500 animate-spin" />
                <p className="mt-4 text-secondary-600">
                  Loading opportunities...
                </p>
              </div>
            ) : isError ? (
              <div className="text-center py-20 bg-red-50 rounded-2xl">
                <p className="text-red-600 font-semibold">
                  {error?.message || "Failed to load job listings"}
                </p>
              </div>
            ) : (
              <div className="max-h-[calc(100vh-200px)] overflow-y-auto pr-2 -mr-2">
                <div className="space-y-6">
                  {jobs.length > 0 ? (
                    jobs.map((job) => (
                      <JobSearchCard
                        key={job.id}
                        job={job}
                        onNavigate={onNavigate}
                        isSaved={savedJobsMap.has(job.id)}
                        onSaveToggle={() => handleSaveToggle(job)}
                        hasSubscription={has_subscription}
                      />
                    ))
                  ) : (
                    <div className="text-center py-20 bg-white rounded-2xl shadow-lg border border-gray-100">
                      <Building className="w-16 h-16 mx-auto text-secondary-300" />
                      <h3 className="mt-4 text-xl font-semibold">
                        No Jobs Found
                      </h3>
                      <p className="mt-2 text-secondary-600">
                        Try adjusting your search terms or check back later.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
            {pagination.count > 0 && jobs.length > 0 && (
              <div className="mt-8 flex justify-between items-center">
                <Button
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={!pagination.previous}
                  variant="secondary"
                  icon={<ArrowLeft size={16} />}
                >
                  Previous
                </Button>
                <span className="text-sm text-gray-600">
                  Page {pagination.currentPage}
                </span>
                <Button
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={!pagination.next}
                  variant="secondary"
                  icon={<ArrowRight size={16} />}
                  iconPosition="right"
                >
                  Next
                </Button>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default JobSearchPage;
